<template>
    <div class="card">
        <div class="card-body">
            <div class="top">
                <h4 class="title text-primary mb-0">{{ __('According to Location') }}</h4>
                <div class="all-selects">
                    <select v-model="selected.division_id" @change="handleDivisionChange()" class="form-select">
                        <option :value="null">{{ __('Select Any') }}</option>
                        <option v-for="division in divisions" :value="division.id" :key="division.id">
                            {{ locale_attr(division, 'name_{lang}') }}
                        </option>
                    </select>
                    <select v-model="selected.district_id" @change="handleDistrictChange()" class="form-select">
                        <option :value="null">{{ __('Select Any') }}</option>
                        <option v-for="district in districts" :value="district.id" :key="district.id">
                            {{ locale_attr(district, 'name_{lang}') }}
                        </option>
                    </select>
                    <select v-model="selected.upazila_id" @change="handleUpazilaChange()" class="form-select">
                        <option :value="null">{{ __('Select Any') }}</option>
                        <option v-for="upazila in upazilas" :value="upazila.id" :key="upazila.id">
                            {{ locale_attr(upazila, 'name_{lang}') }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="individual-data">
                <div class="row">
                    <div class="col-md-4">
                        <template v-if="loadingCountsByDivision">
                            <Spinner spinner-type="grow" />
                        </template>
                        <template v-else-if="divisionChartConfig.series.length">
                            <h5 class="semi-title">
                                {{ selectedDivisionName }}
                                {{ __('Division') + __(':') }}
                                {{ en_to_bn(divisionUserCounts.counts.total_user_count) }}
                            </h5>
                            <ul class="lists">
                                <li v-for="obj in divisionUserCounts.counts.data">
                                    {{ locale_attr(obj, 'type_{lang}_name') + __(':') }}
                                    <span>{{ en_to_bn(obj.total_count) }}</span>
                                </li>
                            </ul>
                            <Chart :options="divisionChartConfig" />
                        </template>
                        <template v-else>
                            <div class="no-data">{{ __('No data found') }}</div>
                        </template>
                    </div>
                    <div class="col-md-4">
                        <template v-if="loadingCountsByDistrict">
                            <Spinner spinner-type="grow" />
                        </template>
                        <template v-else-if="districtChartConfig.series.length">
                            <h5 class="semi-title">
                                {{ selectedDistrictName }}
                                {{ __('District') + __(':') }}
                                {{ en_to_bn(districtUserCounts.counts.total_user_count) }}
                            </h5>
                            <ul class="lists">
                                <li v-for="obj in districtUserCounts.counts.data">
                                    {{ locale_attr(obj, 'type_{lang}_name') + __(':') }}
                                    <span>{{ en_to_bn(obj.total_count) }}</span>
                                </li>
                            </ul>
                            <Chart :options="districtChartConfig" />
                        </template>
                        <template v-else>
                            <div class="no-data">{{ __('No data found') }}</div>
                        </template>
                    </div>
                    <div class="col-md-4">
                        <template v-if="loadingCountsByUpazila">
                            <Spinner spinner-type="grow" />
                        </template>
                        <template v-else-if="upazilaChartConfig.series.length">
                            <h5 class="semi-title">
                                {{ selectedUpazilaName + __(':') }}
                                {{ en_to_bn(upazilaUserCounts.counts.total_user_count) }}
                            </h5>
                            <ul class="lists">
                                <li v-for="obj in upazilaUserCounts.counts.data">
                                    {{ locale_attr(obj, 'type_{lang}_name') + __(':') }}
                                    <span>{{ en_to_bn(obj.total_count) }}</span>
                                </li>
                            </ul>
                            <Chart :options="upazilaChartConfig" />
                        </template>
                        <template v-else>
                            <div class="no-data">{{ __('No data found') }}</div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import axios from "axios";
import { Chart } from 'highcharts-vue';
import Spinner from '../../components/Spinner';

export default {
    components: {
        Spinner,
        Chart,
    },

    inject: ['defaultLocationIds'],

    data() {
        return {
            loadingCountsByDivision: false,
            loadingCountsByDistrict: false,
            loadingCountsByUpazila: false,
            divisions: [],
            districts: [],
            upazilas: [],
            selected: {
                division_id: null,
                district_id: null,
                upazila_id: null,
            },
            divisionUserCounts: {
                counts: {
                    data: [],
                    total_user_count: 0,
                },
            },
            districtUserCounts: {
                counts: {
                    data: [],
                    total_user_count: 0,
                },
            },
            upazilaUserCounts: {
                counts: {
                    data: [],
                    total_user_count: 0,
                },
            },
            divisionChartConfig: this.getChartConfig(),
            districtChartConfig: this.getChartConfig(),
            upazilaChartConfig: this.getChartConfig(),
        }
    },

    computed: {
        selectedDivisionName() {
            const division = this.divisions.find(x => x.id === this.selected.division_id);
            return String(this.locale_attr(division, 'name_{lang}')).trim();
        },
        selectedDistrictName() {
            const district = this.districts.find(x => x.id === this.selected.district_id);
            return String(this.locale_attr(district, 'name_{lang}')).trim();
        },
        selectedUpazilaName() {
            const upazila = this.upazilas.find(x => x.id === this.selected.upazila_id);
            return String(this.locale_attr(upazila, 'name_{lang}')).trim();
        },
    },

    mounted() {
        this.observe_visibility(this.$el, () => {
            this.fetchDivisions();
            Object.keys(this.selected).forEach(key => {
                this.selected[key] = this.defaultLocationIds[key];
            });
            this.handleDivisionChange(false);
            this.handleDistrictChange(false);
            this.handleUpazilaChange(false);
        });
    },

    methods: {
        async handleDivisionChange(resetDependents = true) {
            if (resetDependents) {
                this.selected.district_id = null;
                this.selected.upazila_id = null;
                this.districts = [];
                this.upazilas = [];
                this.districtChartConfig.xAxis.categories = [];
                this.districtChartConfig.series = [];
                this.upazilaChartConfig.xAxis.categories = [];
                this.upazilaChartConfig.series = [];
            }
            if (!this.selected.division_id) return;
            this.fetchDistricts();
            await this.fetchCountsByDivision();
            const { categories, series } = this.prepareDataForChart(this.divisionUserCounts.counts);
            this.divisionChartConfig.xAxis.categories = categories;
            this.divisionChartConfig.series = series;
        },

        async handleDistrictChange(resetDependents = true) {
            if (resetDependents) {
                this.selected.upazila_id = null;
                this.upazilas = [];
                this.upazilaChartConfig.xAxis.categories = [];
                this.upazilaChartConfig.series = [];
            }
            if (!this.selected.district_id) return;
            this.fetchUpazilas();
            await this.fetchCountsByDistrict();
            const { categories, series } = this.prepareDataForChart(this.districtUserCounts.counts);
            this.districtChartConfig.xAxis.categories = categories;
            this.districtChartConfig.series = series;
        },

        async handleUpazilaChange(resetDependents = true) {
            if (resetDependents) this.upazilaUserCounts = {};
            if (!this.selected.upazila_id) return;
            await this.fetchCountsByUpazila();
            const { categories, series } = this.prepareDataForChart(this.upazilaUserCounts.counts);
            this.upazilaChartConfig.xAxis.categories = categories;
            this.upazilaChartConfig.series = series;
        },

        async fetchDivisions() {
            await axios.get('/api/divisions')
                .then(({ data }) => this.divisions = data.data)
                .catch(console.error);
        },

        async fetchDistricts() {
            const { division_id } = this.selected;
            await axios.get('/api/districts', { params: { division_id } })
                .then(({ data }) => this.districts = data.data)
                .catch(console.error);
        },

        async fetchUpazilas() {
            const { district_id } = this.selected;
            await axios.get('/api/upazilas', { params: { district_id } })
                .then(({ data }) => this.upazilas = data.data)
                .catch(console.error);
        },

        async fetchCountsByDivision() {
            const { division_id } = this.selected;
            this.loadingCountsByDivision = true;
            await axios.get('/dashboard/location-wise-user-count', { params: { division_id } })
                .then(({ data }) => this.divisionUserCounts = data)
                .catch(console.error);

            this.loadingCountsByDivision = false;
        },

        async fetchCountsByDistrict() {
            const { district_id } = this.selected;
            this.loadingCountsByDistrict = true;
            await axios.get('/dashboard/location-wise-user-count', { params: { district_id } })
                .then(({ data }) => this.districtUserCounts = data)
                .catch(console.error);

            this.loadingCountsByDistrict = false;
        },

        async fetchCountsByUpazila() {
            const { upazila_id } = this.selected;
            this.loadingCountsByUpazila = true;
            await axios.get('/dashboard/location-wise-user-count', { params: { upazila_id } })
                .then(({ data }) => this.upazilaUserCounts = data)
                .catch(console.error);

            this.loadingCountsByUpazila = false;
        },

        prepareDataForChart(counts) {
            const categories = [];
            const maleCounts = [];
            const femaleCounts = [];
            const othersGenderCounts = [];

            if (counts instanceof Object && Array.isArray(counts.data)) {
                counts.data.forEach(obj => {
                    categories.push(this.locale_attr(obj, 'type_{lang}_name'));
                    maleCounts.push(Number(obj.male_count));
                    femaleCounts.push(Number(obj.female_count));
                    othersGenderCounts.push(Number(obj.others_gender_count));
                });
            }

            return {
                categories,
                series: [
                    {
                        name: this.__('Male'),
                        data: maleCounts,
                        color: '#008000'
                    },
                    {
                        name: this.__('Female'),
                        data: femaleCounts,
                        color: '#FF359B'
                    },
                    {
                        name: this.__('Others'),
                        data: othersGenderCounts,
                        color: '#B57EDC'
                    },
                ],
            };
        },

        getChartConfig(callback) {
            const that = this;
            const config = {
                chart: {
                    type: 'column',
                },
                title: {
                    enabled: undefined,
                    text: '',
                },
                credits: {
                    enabled: false
                },
                xAxis: {
                    categories: [],
                    crosshair: true,
                    accessibility: {
                        description: 'Institutions'
                    },
                },
                yAxis: {
                    title: {
                        text: null,
                    },
                    labels: {
                        formatter: function () {
                            return that.en_to_bn(this.value);
                        },
                    },
                },
                tooltip: {
                    formatter: function () {
                        return that.en_to_bn(this.y);
                    },
                    backgroundColor: '#070C3CE5',
                    style: {
                        color: '#FFFFFF',
                    },
                },
                plotOptions: {
                    column: {
                        pointPadding: 0.2,
                        borderWidth: 0,
                    },
                },
                series: [],
                accessibility: {
                    enabled: false,
                },
            };

            if (typeof callback === 'function') {
                callback(config);
            }

            return _.cloneDeep(config);
        },
    },
}
</script>

<style scoped lang="scss">
.no-data {
    min-height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>